import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Mail, Phone, User, Building2, Calendar, CheckCircle, XCircle, Clock, KeyRound, Copy, ArrowRightLeft, History, Trash2, Edit, Star } from 'lucide-react';
import { formatPhoneDisplay } from '@/utils/phoneUtils';
import AdminProfileEditor from '@/components/AdminProfileEditor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AgentDetail = () => {
  const navigate = useNavigate();
  const { agentId } = useParams();
  const [agent, setAgent] = useState(null);
  const [broker, setBroker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [resetPasswordData, setResetPasswordData] = useState(null);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [brokers, setBrokers] = useState([]);
  const [selectedBrokerId, setSelectedBrokerId] = useState('');
  const [transferring, setTransferring] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletePasscode, setDeletePasscode] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showProfileEditor, setShowProfileEditor] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/login');
      return;
    }
    fetchAgentDetails();
  }, [agentId]);

  const fetchAgentDetails = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await axios.get(`${API}/admin/agents/${agentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgent(response.data);
      
      // Fetch broker info if agent has broker_id
      if (response.data.broker_id) {
        try {
          const brokerResponse = await axios.get(`${API}/admin/brokers/${response.data.broker_id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setBroker(brokerResponse.data);
        } catch (error) {
          console.error('Failed to fetch broker info:', error);
        }
      }
    } catch (error) {
      toast.error('Failed to fetch agent details');
      if (error.response?.status === 404) {
        navigate('/admin/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await axios.post(
        `${API}/admin/users/${agent.id}/reset-password`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResetPasswordData(response.data);
      setShowPasswordDialog(true);
      toast.success('Password reset successfully!');
    } catch (error) {
      toast.error('Failed to reset password');
      console.error(error);
    }
  };

  const copyPassword = () => {
    if (resetPasswordData?.new_password) {
      navigator.clipboard.writeText(resetPasswordData.new_password);
      toast.success('Password copied to clipboard!');
    }
  };

  const handleStatusChange = async (newStatus) => {
    const token = localStorage.getItem('adminToken');
    try {
      await axios.patch(
        `${API}/admin/agents/${agent.id}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAgent({ ...agent, status: newStatus });
      toast.success(`Agent ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      toast.error('Failed to update agent status');
      console.error(error);
    }
  };

  const openTransferDialog = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      const res = await axios.get(`${API}/admin/brokers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBrokers(res.data || []);
      setSelectedBrokerId('');
      setShowTransferDialog(true);
    } catch (error) {
      toast.error('Failed to load brokers');
    }
  };

  const handleTransfer = async () => {
    if (!selectedBrokerId) {
      toast.error('Please select a broker');
      return;
    }
    const token = localStorage.getItem('adminToken');
    setTransferring(true);
    try {
      const res = await axios.post(
        `${API}/admin/agents/${agent.id}/transfer-broker`,
        { new_broker_id: selectedBrokerId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Agent transferred to ${res.data.new_broker_name}`);
      setShowTransferDialog(false);
      fetchAgentDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Transfer failed');
    } finally {
      setTransferring(false);
    }
  };

  const handleDeleteAgent = async () => {
    setDeleteLoading(true);
    const token = localStorage.getItem('adminToken');
    try {
      await axios.delete(`${API}/admin/agents/${agentId}`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { passcode: deletePasscode }
      });
      toast.success('Agent deleted');
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleteLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: { icon: CheckCircle, label: 'Active', color: 'text-green-700', bgColor: 'bg-green-100' },
      inactive: { icon: XCircle, label: 'Inactive', color: 'text-red-700', bgColor: 'bg-red-100' },
      pending: { icon: Clock, label: 'Pending', color: 'text-yellow-700', bgColor: 'bg-yellow-100' }
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${badge.bgColor} ${badge.color}`}>
        <Icon size={14} />
        {badge.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <p className="text-stone-600">Loading agent details...</p>
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <p className="text-stone-600">Agent not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <div className="bg-white border-b border-stone-200 py-4 sm:py-6 px-4 sm:px-8">
        <div className="max-w-7xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/admin/dashboard')}
            className="mb-4 text-stone-600 hover:text-stone-900"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Dashboard
          </Button>
          
          <div className="flex flex-col gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#1A4D2E] mb-2" style={{fontFamily: '"Playfair Display", serif'}}>
                Agent Details
              </h1>
              <p className="text-stone-600">View and manage agent information</p>
            </div>
            
            <div className="flex flex-wrap gap-2">
              {agent.role === 'agent' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openTransferDialog}
                  className="border-2 border-blue-200 text-blue-700 hover:bg-blue-50"
                  data-testid="transfer-broker-btn"
                >
                  <ArrowRightLeft size={16} className="mr-1.5" />
                  Transfer Broker
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={handleResetPassword}
                className="border-2"
              >
                <KeyRound size={16} className="mr-1.5" />
                Reset Password
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowProfileEditor(true)}
                className="border-2 border-[#1A4D2E] text-[#1A4D2E] hover:bg-[#1A4D2E]/10"
                data-testid="edit-public-profile-btn"
              >
                <Edit size={16} className="mr-1.5" />
                Edit Public Profile
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('adminToken');
                    const res = await axios.put(`${API}/admin/agents/${agentId}/premium`, { is_premium: !agent.is_premium }, { headers: { Authorization: `Bearer ${token}` } });
                    setAgent(prev => ({ ...prev, is_premium: res.data.is_premium }));
                    toast.success(res.data.is_premium ? 'Agent marked as Premium' : 'Premium removed');
                  } catch { toast.error('Failed to update'); }
                }}
                className={`border-2 ${agent.is_premium ? 'border-amber-400 text-amber-700 bg-amber-50 hover:bg-amber-100' : 'border-stone-300 text-stone-600 hover:bg-stone-50'}`}
                data-testid="toggle-premium-btn"
              >
                <Star size={16} className="mr-1.5" fill={agent.is_premium ? 'currentColor' : 'none'} />
                {agent.is_premium ? 'Premium Agent' : 'Set Premium'}
              </Button>
              
              {agent.status === 'active' ? (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => handleStatusChange('inactive')}
                >
                  <XCircle size={16} className="mr-1.5" />
                  Deactivate
                </Button>
              ) : (
                <Button
                  size="sm"
                  onClick={() => handleStatusChange('active')}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <CheckCircle size={16} className="mr-1.5" />
                  Activate
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowDeleteDialog(true)}
                className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                data-testid="delete-agent-btn"
              >
                <Trash2 size={16} className="mr-1.5" />
                Delete
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Agent Information */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="bg-white p-6 rounded-2xl card-shadow">
              <h2 className="text-2xl font-bold text-[#1A4D2E] mb-6">Agent Information</h2>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between py-3 border-b border-stone-100">
                  <div className="flex items-center gap-3">
                    <User size={20} className="text-stone-400" />
                    <span className="text-stone-600 font-medium">Full Name</span>
                  </div>
                  <span className="text-stone-900 font-semibold">{agent.name}</span>
                </div>

                <div className="flex items-center justify-between py-3 border-b border-stone-100">
                  <div className="flex items-center gap-3">
                    <Mail size={20} className="text-stone-400" />
                    <span className="text-stone-600 font-medium">Email</span>
                  </div>
                  <span className="text-stone-900">{agent.email}</span>
                </div>

                <div className="flex items-center justify-between py-3 border-b border-stone-100">
                  <div className="flex items-center gap-3">
                    <Phone size={20} className="text-stone-400" />
                    <span className="text-stone-600 font-medium">Phone</span>
                  </div>
                  <span className="text-stone-900">{agent.phone ? formatPhoneDisplay(agent.phone) : 'Not provided'}</span>
                </div>

                <div className="flex items-center justify-between py-3 border-b border-stone-100">
                  <div className="flex items-center gap-3">
                    <Calendar size={20} className="text-stone-400" />
                    <span className="text-stone-600 font-medium">Joined Date</span>
                  </div>
                  <span className="text-stone-900">{new Date(agent.created_at).toLocaleDateString()}</span>
                </div>

                <div className="flex items-center justify-between py-3 border-b border-stone-100">
                  <div className="flex items-center gap-3">
                    <CheckCircle size={20} className="text-stone-400" />
                    <span className="text-stone-600 font-medium">Status</span>
                  </div>
                  {getStatusBadge(agent.status)}
                </div>

                <div className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-3">
                    <span className="text-stone-600 font-medium">Agent ID</span>
                  </div>
                  <span className="text-stone-500 text-sm font-mono">{agent.id}</span>
                </div>
              </div>
            </Card>

            {/* Privacy Settings */}
            <Card className="bg-white p-6 rounded-2xl card-shadow">
              <h2 className="text-2xl font-bold text-[#1A4D2E] mb-6">Privacy Settings</h2>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-3">
                    <Clock size={20} className="text-stone-400" />
                    <span className="text-stone-600 font-medium">Timezone</span>
                  </div>
                  <span className="text-stone-900">{agent.timezone || 'America/New_York'}</span>
                </div>
              </div>
            </Card>
          </div>

          {/* Broker Information */}
          <div>
            <Card className="bg-white p-6 rounded-2xl card-shadow">
              <h2 className="text-2xl font-bold text-[#1A4D2E] mb-6">Managing Broker</h2>
              
              {broker ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 bg-purple-50 rounded-lg">
                    <Building2 size={24} className="text-purple-600" />
                    <div>
                      <p className="font-semibold text-stone-900">{broker.name}</p>
                      <p className="text-sm text-stone-600">{broker.email}</p>
                    </div>
                  </div>

                  {broker.phone && (
                    <div className="flex items-center justify-between py-3 border-b border-stone-100">
                      <span className="text-stone-600 text-sm">Phone</span>
                      <span className="text-stone-900 text-sm">{formatPhoneDisplay(broker.phone)}</span>
                    </div>
                  )}

                  {broker.office_address && (
                    <div className="py-3 border-b border-stone-100">
                      <span className="text-stone-600 text-sm block mb-1">Office Address</span>
                      <span className="text-stone-900 text-sm">{broker.office_address}</span>
                    </div>
                  )}

                  {broker.broker_license_id && (
                    <div className="flex items-center justify-between py-3">
                      <span className="text-stone-600 text-sm">License ID</span>
                      <span className="text-stone-900 text-sm font-mono">{broker.broker_license_id}</span>
                    </div>
                  )}

                  <Button
                    variant="outline"
                    onClick={() => navigate(`/admin/brokers/${broker.id}`)}
                    className="w-full mt-4"
                  >
                    View Broker Details
                  </Button>
                </div>
              ) : (
                <div className="text-center py-8 text-stone-500">
                  <p>No managing broker assigned</p>
                </div>
              )}
            </Card>
          </div>
        </div>

        {/* Broker History */}
        {agent.broker_history && agent.broker_history.length > 0 && (
          <div className="mt-6">
            <Card className="bg-white p-6 rounded-2xl card-shadow" data-testid="broker-history-card">
              <h2 className="text-xl font-bold text-[#1A4D2E] mb-4 flex items-center gap-2">
                <History size={20} /> Broker Transfer History
              </h2>
              <div className="space-y-3">
                {agent.broker_history.map((entry, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg border border-stone-100" data-testid={`broker-history-entry-${idx}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-stone-200 flex items-center justify-center text-stone-600 text-sm font-bold">
                        {idx + 1}
                      </div>
                      <div>
                        <p className="font-medium text-stone-900">{entry.broker_name || 'Unknown Broker'}</p>
                        <p className="text-xs text-stone-500 font-mono">{entry.broker_id}</p>
                      </div>
                    </div>
                    <div className="text-right text-sm">
                      <p className="text-stone-600">
                        {new Date(entry.from).toLocaleDateString()} — {new Date(entry.to).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}
      </div>

      {/* Password Reset Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#1A4D2E]">Password Reset Successful</DialogTitle>
            <DialogDescription>
              The agent's password has been reset. Please share this password securely with the agent.
            </DialogDescription>
          </DialogHeader>
          
          {resetPasswordData && (
            <div className="space-y-4">
              <div className="bg-stone-50 p-4 rounded-lg">
                <p className="text-sm text-stone-600 mb-2">New Password:</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-white px-3 py-2 rounded border border-stone-200 font-mono text-lg">
                    {resetPasswordData.new_password}
                  </code>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={copyPassword}
                  >
                    <Copy size={16} />
                  </Button>
                </div>
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-lg">
                <p className="text-sm text-yellow-800">
                  ⚠️ <strong>Important:</strong> Save this password now. You won't be able to see it again.
                </p>
              </div>
            </div>
          )}
          
          <div className="flex justify-end">
            <Button onClick={() => setShowPasswordDialog(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Transfer Broker Dialog */}
      <Dialog open={showTransferDialog} onOpenChange={setShowTransferDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#1A4D2E]">Transfer Agent to New Broker</DialogTitle>
            <DialogDescription>
              Select a new broker for <strong>{agent?.name}</strong>. The old broker will retain visibility of all historical data created during their tenure.
            </DialogDescription>
          </DialogHeader>

          {agent?.broker_id && (
            <div className="bg-stone-50 p-3 rounded-lg mb-2">
              <p className="text-xs text-stone-500 uppercase tracking-wide mb-1">Current Broker</p>
              <p className="text-sm font-semibold text-stone-800">{agent.broker_name || agent.broker_id}</p>
            </div>
          )}

          <div>
            <label className="text-sm font-medium text-stone-700 block mb-2">New Broker</label>
            <select
              value={selectedBrokerId}
              onChange={(e) => setSelectedBrokerId(e.target.value)}
              className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm bg-white"
              data-testid="transfer-broker-select"
            >
              <option value="">Select a broker...</option>
              {brokers
                .filter(b => b.id !== agent?.broker_id)
                .map(b => (
                  <option key={b.id} value={b.id}>
                    {b.full_name || b.name || b.email} {b.status !== 'active' ? `(${b.status})` : ''}
                  </option>
                ))
              }
            </select>
          </div>

          <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg text-sm text-amber-800">
            <strong>Note:</strong> After transfer, the previous broker will still see all events, leads, and data created during their time with this agent.
          </div>

          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setShowTransferDialog(false)}>Cancel</Button>
            <Button
              onClick={handleTransfer}
              disabled={!selectedBrokerId || transferring}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="confirm-transfer-btn"
            >
              {transferring ? 'Transferring...' : 'Confirm Transfer'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-red-600">Delete Agent</DialogTitle>
            <DialogDescription>
              This will permanently delete {agent?.name}. Enter admin passcode to confirm.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <input
              type="password"
              placeholder="Enter admin passcode"
              value={deletePasscode}
              onChange={(e) => setDeletePasscode(e.target.value)}
              className="w-full px-3 py-2 border border-stone-300 rounded-lg bg-white text-sm"
              data-testid="delete-passcode-input"
            />
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setShowDeleteDialog(false); setDeletePasscode(''); }} className="flex-1">
                Cancel
              </Button>
              <Button
                onClick={handleDeleteAgent}
                disabled={deleteLoading || !deletePasscode}
                variant="destructive"
                className="flex-1"
                data-testid="confirm-delete-btn"
              >
                {deleteLoading ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AdminProfileEditor
        open={showProfileEditor}
        onClose={(saved) => { setShowProfileEditor(false); if (saved) fetchAgentDetails(); }}
        userId={agentId}
        userType={agent?.role === 'broker' ? 'agent' : 'agent'}
      />
    </div>
  );
};

export default AgentDetail;
